import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { ArrowLeft, CalendarClock, Loader2, ShieldCheck, Video, Phone } from "lucide-react";
import { toast } from "sonner";
import SlotPicker from "@/components/SlotPicker";

export default function BookAndPay() {
  const [params] = useSearchParams();
  const offerKey = params.get("offer");
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/plans"), api.get("/booking/slots"), api.get("/booking/current").catch(() => ({ data: {} }))])
      .then(([plansRes, slotsRes, cur]) => {
        const p = plansRes.data;
        const all = [
          ...(p.rdv_unique ? [{ ...p.rdv_unique, requires_booking: true }] : []),
          ...(p.extra_offers || []),
        ];
        const found = all.find((o) => o.key === offerKey);
        setOffer(found || null);
        setSlots(slotsRes.data.slots || []);
        if (cur.data?.phone) setPhone(cur.data.phone);
      })
      .finally(() => setReady(true));
  }, [offerKey]);

  const price = useMemo(() => (offer ? (offer.promo_price || offer.price) : null), [offer]);

  const pay = async () => {
    if (!selected) { toast.error("Choisis un créneau."); return; }
    setLoading(true);
    try {
      const r = await api.post("/payments/checkout", {
        plan: offerKey, origin_url: window.location.origin,
        slot_id: selected.id, phone: phone.trim(), note: note.trim(),
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de paiement");
      setLoading(false);
    }
  };

  return (
    <ClientLayout>
      <button onClick={() => navigate("/tarifs")} className="inline-flex items-center gap-1.5 text-sm text-obsidian/50 hover:text-obsidian"><ArrowLeft size={15} /> Retour aux offres</button>
      <div className="fade-up mt-4 max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-sage/12 text-sage px-3 py-1 text-xs font-semibold"><CalendarClock size={13} /> Réservation sur rendez-vous</div>
        {!ready ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-obsidian/40" /></div>
        ) : !offer ? (
          <p className="mt-6 text-obsidian/60">Cette prestation n'est plus disponible. <button className="underline" onClick={() => navigate("/tarifs")}>Voir les offres</button>.</p>
        ) : (
          <>
            <h1 className="mt-4 text-3xl font-light tracking-tight" data-testid="book-offer-title">{offer.label} · {price} €</h1>
            {offer.description && <p className="mt-2 text-obsidian/60">{offer.description}</p>}
            <p className="mt-2 text-sm text-obsidian/50">1. Choisis ton créneau · 2. Paie pour le réserver · 3. Confirmation + lien de visio par email.</p>

            <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-obsidian/50">Créneaux disponibles</h2>
            {slots.length === 0 ? (
              <div data-testid="no-slots" className="mt-3 rounded-2xl border border-border bg-white p-6 text-sm text-obsidian/60">
                Aucun créneau ouvert pour le moment. Reviens un peu plus tard — Ton Coach en ouvre régulièrement.
              </div>
            ) : (
              <div className="mt-3">
                <SlotPicker slots={slots} selected={selected} onSelect={setSelected} testidPrefix="book-slot" />
              </div>
            )}

            {slots.length > 0 && (
              <div className="mt-6 space-y-3" data-testid="book-contact">
                <input data-testid="book-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ton téléphone (pour un RDV tél. ou en secours)" className="w-full rounded-xl border border-border px-4 py-3 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
                <textarea data-testid="book-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Un point à aborder avec Ton Coach ? (optionnel)" className="w-full rounded-xl border border-border px-4 py-3 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
              </div>
            )}

            <button data-testid="book-pay-button" onClick={pay} disabled={!selected || loading || slots.length === 0}
              className="mt-6 w-full rounded-full bg-obsidian text-white py-4 font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-40 disabled:hover:translate-y-0">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <CalendarClock size={16} />}
              {selected ? `Payer & réserver · ${price} €` : "Choisis un créneau"}
            </button>
            <p className="mt-3 text-xs text-obsidian/40 flex items-center justify-center gap-1.5"><ShieldCheck size={13} strokeWidth={1.6} /> Paiement 100% sécurisé par Stripe. Le créneau est bloqué le temps du paiement.</p>
          </>
        )}
      </div>
    </ClientLayout>
  );
}
